/**
 * Newsletter Subscribers Tab Component
 * V2.0 - Enhanced with analytics dashboard, funnel, charts
 * Displays and manages newsletter subscribers from Coming Soon modal and other capture forms
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Send as SendIcon,
  MailOutline as MailOutlineIcon,
  TouchApp as TouchAppIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import apiService from '../../services/api';
import MemberHealthBanner from './components/MemberHealthBanner';
import AlertBanner from './components/AlertBanner';
import KpiCardGrid from './components/KpiCardGrid';
import ConversionFunnel from './components/ConversionFunnel';

interface NewsletterSubscriber {
  id: string;
  email: string;
  source: string;
  coupon_code: string;
  is_active: boolean;
  email_sent: boolean;
  converted_to_user: boolean;
  converted_at: string | null;
  created_at: string;
}

interface NewsletterStats {
  total: number;
  active: number;
  converted: number;
  emails_sent: number;
  by_source: Record<string, { count: number; active: number; converted: number }>;
}

interface NewsletterAnalytics {
  health: {
    total_subscribers: number;
    active_subscribers: number;
    emails_sent: number;
    emails_opened: number;
    emails_clicked: number;
    converted: number;
    unconverted_active: number;
    open_rate: string;
    click_rate: string;
    conversion_rate: string;
  };
  funnel: { step: string; value: number }[];
  signup_timeline: { date: string; source: string; count: number }[];
  source_distribution: { source: string; count: number; converted: number; active: number }[];
  coupon_distribution: { coupon_code: string; count: number; converted: number }[];
  email_status: { status: string; count: number }[];
}

const SOURCE_COLORS: Record<string, string> = {
  coming_soon_modal: '#1A5F7A',
  website_popup: '#059669',
  footer_signup: '#D4AC0D',
  social_media: '#7c3aed',
  referral: '#dc2626',
  unknown: '#94a3b8',
};

const EMAIL_STATUS_COLORS = ['#059669', '#D4AC0D', '#94a3b8'];

export const NewsletterSubscribersTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [stats, setStats] = useState<NewsletterStats | null>(null);
  const [analytics, setAnalytics] = useState<NewsletterAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [convertedFilter, setConvertedFilter] = useState('');
  const [emailSentFilter, setEmailSentFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const loadSubscribers = async () => {
    try {
      const response = await apiService.getNewsletterSubscribers({
        page: page + 1,
        limit: pageSize,
        search,
        source: sourceFilter,
        status: statusFilter,
        converted: convertedFilter,
        email_sent: emailSentFilter,
      });
      setSubscribers(response.subscribers || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      if (error instanceof Error && error.message !== 'Unauthorized') {
        dispatch(addNotification({ type: 'error', message: error instanceof Error ? error.message : 'Failed to load subscribers' }));
      }
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await apiService.getNewsletterSubscriberStats();
      setStats(statsData);
    } catch (error) {
      console.warn('Could not load newsletter stats');
    }
  };

  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const data = await apiService.getNewsletterAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.warn('Could not load newsletter analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadSubscribers(), loadStats(), loadAnalytics()]);
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    dispatch(addNotification({ type: 'success', message: 'Newsletter subscriber data refreshed' }));
  };

  const handleExport = async () => {
    try {
      await apiService.exportNewsletterSubscribers('csv', { source: sourceFilter, status: statusFilter });
      dispatch(addNotification({ type: 'success', message: 'Newsletter subscribers exported successfully' }));
    } catch (error) {
      dispatch(addNotification({ type: 'error', message: 'Failed to export newsletter subscribers' }));
    }
  };

  useEffect(() => {
    loadData();
  }, [page, pageSize, search, sourceFilter, statusFilter, convertedFilter, emailSentFilter]);

  // Prepare chart data
  const sourceDonutData = (analytics?.source_distribution || []).map(s => ({
    name: s.source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value: s.count,
    fill: SOURCE_COLORS[s.source] || '#94a3b8',
  }));

  const emailStatusDonutData = (analytics?.email_status || []).map((s, i) => ({
    name: s.status,
    value: s.count,
    fill: EMAIL_STATUS_COLORS[i] || '#94a3b8',
  }));

  // Build timeline data: aggregate by date with separate source lines
  const timelineMap = new Map<string, Record<string, number>>();
  const allSources = new Set<string>();
  (analytics?.signup_timeline || []).forEach(entry => {
    const dateStr = new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!timelineMap.has(dateStr)) timelineMap.set(dateStr, {});
    const rec = timelineMap.get(dateStr)!;
    rec[entry.source] = (rec[entry.source] || 0) + entry.count;
    allSources.add(entry.source);
  });
  const timelineData = Array.from(timelineMap.entries()).map(([date, sources]) => ({
    date,
    ...sources,
  }));
  const sourceList = Array.from(allSources);

  const columns: GridColDef[] = [
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 250 },
    {
      field: 'source',
      headerName: 'Source',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || 'Unknown'}
          size="small"
          sx={{
            bgcolor: `${SOURCE_COLORS[params.value as string] || '#94a3b8'}20`,
            color: SOURCE_COLORS[params.value as string] || '#94a3b8',
            fontWeight: 600,
            border: `1px solid ${SOURCE_COLORS[params.value as string] || '#94a3b8'}40`,
          }}
        />
      ),
    },
    {
      field: 'coupon_code',
      headerName: 'Coupon Code',
      width: 130,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: '#D4AC0D20',
              color: '#b8860b',
              fontWeight: 700,
              border: '1px solid #D4AC0D40',
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">{'\u2014'}</Typography>
        ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label="Active" size="small" color="success" icon={<CheckCircleIcon />} />
        ) : (
          <Chip label="Unsubscribed" size="small" color="default" icon={<CancelIcon />} />
        ),
    },
    {
      field: 'email_sent',
      headerName: 'Email Sent',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? <Chip label="Yes" size="small" color="info" /> : <Chip label="No" size="small" color="default" />,
    },
    {
      field: 'converted_to_user',
      headerName: 'Converted',
      width: 120,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label="Converted" size="small" color="success" icon={<CheckCircleIcon />} sx={{ fontWeight: 600 }} />
        ) : (
          <Chip label="No" size="small" color="default" />
        ),
    },
    {
      field: 'created_at',
      headerName: 'Signed Up',
      width: 160,
      renderCell: (params: GridRenderCellParams) =>
        new Date(params.value as string).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    },
  ];

  if (loading && !analytics) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Newsletter Subscriber Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analytics and management for newsletter capture forms and email campaigns
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* === ANALYTICS DASHBOARD === */}
      {analyticsLoading ? (
        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mb: 2 }} />
      ) : analytics && (
        <>
          {/* Health Banner */}
          <MemberHealthBanner
            gradient="linear-gradient(135deg, #0f3d50, #1A5F7A)"
            items={[
              { value: analytics.health.total_subscribers.toLocaleString(), label: 'TOTAL SUBSCRIBERS', delta: `${analytics.health.active_subscribers} active`, deltaColor: '#6ee7b7' },
              { value: analytics.health.active_subscribers.toLocaleString(), label: 'ACTIVE', delta: `${analytics.health.total_subscribers > 0 ? ((analytics.health.active_subscribers / analytics.health.total_subscribers) * 100).toFixed(0) : 0}% retention`, deltaColor: '#6ee7b7' },
              { value: analytics.health.emails_sent.toLocaleString(), label: 'EMAILS SENT', delta: `${analytics.health.open_rate}% open rate`, deltaColor: 'rgba(255,255,255,0.5)' },
              { value: `${analytics.health.open_rate}%`, label: 'OPEN RATE', delta: `${analytics.health.emails_opened} opened`, deltaColor: '#6ee7b7' },
              { value: analytics.health.converted.toLocaleString(), label: 'CONVERTED', delta: `${analytics.health.conversion_rate}% conv. rate`, deltaColor: '#6ee7b7' },
              { value: analytics.health.unconverted_active.toLocaleString(), label: 'POTENTIAL LEADS', delta: 'Active, unconverted', deltaColor: '#fbbf24' },
            ]}
          />

          {/* Conversion Opportunity Alert */}
          <AlertBanner
            show={analytics.health.unconverted_active > 0}
            gradient="linear-gradient(135deg, #78350f, #d97706)"
            icon={<TrendingUpIcon />}
            title={`${analytics.health.unconverted_active} Conversion Opportunities`}
            subtitle="Active subscribers who haven't converted to platform members yet"
            stats={[
              { value: analytics.health.unconverted_active, label: 'Leads' },
              { value: `${analytics.health.conversion_rate}%`, label: 'Conv Rate' },
            ]}
          />

          {/* Conversion Funnel */}
          <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <ConversionFunnel
              title="Email-to-Member Conversion Funnel"
              steps={[
                { label: 'Captured', value: analytics.health.total_subscribers, color: '#1A5F7A', icon: <PeopleIcon sx={{ fontSize: 14 }} /> },
                { label: 'Email Sent', value: analytics.health.emails_sent, color: '#0284c7', icon: <SendIcon sx={{ fontSize: 14 }} /> },
                { label: 'Opened', value: analytics.health.emails_opened, color: '#059669', icon: <MailOutlineIcon sx={{ fontSize: 14 }} /> },
                { label: 'Clicked', value: analytics.health.emails_clicked, color: '#D4AC0D', icon: <TouchAppIcon sx={{ fontSize: 14 }} /> },
                { label: 'Converted', value: analytics.health.converted, color: '#dc2626', icon: <PersonAddIcon sx={{ fontSize: 14 }} /> },
              ]}
            />
          </Paper>

          {/* KPI Cards */}
          <KpiCardGrid
            columns={3}
            cards={[
              { label: 'Active Rate', value: `${analytics.health.total_subscribers > 0 ? ((analytics.health.active_subscribers / analytics.health.total_subscribers) * 100).toFixed(1) : 0}%`, subtitle: `${analytics.health.active_subscribers} of ${analytics.health.total_subscribers}`, topBorderColor: '#059669' },
              { label: 'Open Rate', value: `${analytics.health.open_rate}%`, subtitle: `${analytics.health.emails_opened} of ${analytics.health.emails_sent} sent`, topBorderColor: '#0284c7' },
              { label: 'Click Rate', value: `${analytics.health.click_rate}%`, subtitle: `${analytics.health.emails_clicked} of ${analytics.health.emails_opened} opened`, topBorderColor: '#D4AC0D' },
              { label: 'Conversion Rate', value: `${analytics.health.conversion_rate}%`, subtitle: `${analytics.health.converted} members`, subtitleColor: '#059669', topBorderColor: '#dc2626' },
              { label: 'Unconverted Active', value: analytics.health.unconverted_active.toLocaleString(), subtitle: 'Potential leads', subtitleColor: '#d97706', topBorderColor: '#d97706' },
              { label: 'Total Emails Sent', value: analytics.health.emails_sent.toLocaleString(), subtitle: 'Campaign reach', topBorderColor: '#7c3aed' },
            ]}
          />

          {/* Charts Row: Signup Timeline + Source Distribution */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Signup Timeline by Source
                </Typography>
                <Box sx={{ height: 280, minHeight: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      {sourceList.map((source, idx) => (
                        <Line
                          key={source}
                          type="monotone"
                          dataKey={source}
                          name={source.replace(/_/g, ' ')}
                          stroke={SOURCE_COLORS[source] || `hsl(${idx * 60}, 60%, 50%)`}
                          strokeWidth={2}
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Source Distribution
                </Typography>
                <Box sx={{ height: 280, minHeight: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={sourceDonutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                        {sourceDonutData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                      </Pie>
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 2: Coupon Distribution + Email Status */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Coupon Distribution
                </Typography>
                {(analytics.coupon_distribution || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No coupon data available</Typography>
                ) : (
                  <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                    {analytics.coupon_distribution.map((coupon) => (
                      <Box
                        key={coupon.coupon_code}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: '10px 0',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Chip
                            label={coupon.coupon_code}
                            size="small"
                            sx={{ bgcolor: '#D4AC0D20', color: '#b8860b', fontWeight: 700, border: '1px solid #D4AC0D40' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                            {coupon.count} subscribers
                          </Typography>
                          <Chip
                            label={`${coupon.converted} converted`}
                            size="small"
                            color={coupon.converted > 0 ? 'success' : 'default'}
                            sx={{ fontSize: '11px' }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, mb: 2 }}>
                  Email Delivery Status
                </Typography>
                <Box sx={{ height: 200, minHeight: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={emailStatusDonutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {emailStatusDonutData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                      </Pie>
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* === EXISTING TABLE SECTION === */}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth label="Search Email" variant="outlined" size="small" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Enter email..." />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={(e) => setSourceFilter(e.target.value)}>
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="coming_soon_modal">Coming Soon Modal</MenuItem>
                <MenuItem value="website_popup">Website Popup</MenuItem>
                <MenuItem value="footer_signup">Footer Signup</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Converted</InputLabel>
              <Select value={convertedFilter} label="Converted" onChange={(e) => setConvertedFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Email Sent</InputLabel>
              <Select value={emailSentFilter} label="Email Sent" onChange={(e) => setEmailSentFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ p: 3 }}>
        <DataGrid
          rows={subscribers}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[10, 25, 50, 100]}
          autoHeight
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
        />
      </Paper>
    </Box>
  );
};

export default NewsletterSubscribersTab;
